// Vacation calendar and balance management functionality
// 100% HTTP dependent - would use dedicated vacation endpoints when available
// For now, vacation data comes from employee data (vacationBalance field)
// @ts-nocheck

import { get, del } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders, logApiCall } from './api-utils'
import type {
  VacationBalance,
  VacationBalanceResponse,
  VacationRequestsResponse
} from '@/types/vacation'

// HTTP API calls using AWS Amplify client - exactly like documents.ts
const employeeApiCall = async (endpoint: string, params: Record<string, string> = {}): Promise<any> => {
  const queryParams = new URLSearchParams(params)
  const path = `/monumenta/employees${endpoint ? `/${endpoint}` : ''}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
  
  logApiCall(path)
  
  const { response } = await get({
    apiName: 'monumenta',
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })
  
  const result = await response
  return await result.body.json()
}

// ============================================================================
// 🚀 VACATION TYPES AND INTERFACES
// ============================================================================

export interface VacationEvent {
  id: string;
  employeeName: string;
  department: string;
  type: 'scheduled' | 'leave' | 'timeoff';
  startDate: string;
  endDate: string;
  days: number;
  status: 'approved' | 'pending' | 'rejected';
  description: string;
}

// Legacy interface for backward compatibility - deprecated, use VacationBalance from @/types/vacation instead
export interface LegacyVacationBalance {
  name: string;
  daysAvailable: number;
  daysUsed: number;
  daysRemaining: number;
  period: string;
  department: string;
  role: string;
  saldoReal: number;
}

export interface VacationBalanceStatistics {
  totalEmployees: number;
  totalDaysRemaining: number;
  totalDaysUsed: number;
  averageDaysRemaining: number;
  averageDaysUsed: number;
  highBalanceCount: number;
  lowBalanceCount: number;
  utilizationRate: number;
}

export interface VacationTrends {
  monthlyTotals: Array<{
    month: number;
    totalDays: number;
    eventCount: number;
  }>;
  peakMonths: Array<{
    month: number;
    totalDays: number;
  }>;
  totalVacationDays: number;
}

export interface DepartmentVacationUtilization {
  department: string;
  employeeCount: number;
  totalDaysUsed: number;
  totalDaysRemaining: number;
  utilizationRate: number;
  avgDaysRemaining: number;
  upcomingEvents: number;
}

export interface VacationRecommendations {
  urgentActions: string[];
  departmentAlerts: string[];
  generalRecommendations: string[];
  riskEmployees: Array<{
    name: string;
    daysRemaining: number;
    department: string;
    riskLevel: 'high' | 'medium' | 'low';
  }>;
}

// ============================================================================
// 🚀 VACATION DATA
// ============================================================================

// Removed: TECH_TEAM_VACATION constant - now using real API data from backend

// Removed: VACATION_CALENDAR_EVENTS constant - now using real API data from backend

// ============================================================================
// 🚀 VACATION QUERY FUNCTIONS
// ============================================================================

// Removed: All helper functions that used VACATION_CALENDAR_EVENTS constant
// - getVacationEventsByMonth()
// - getVacationEventsByType()
// - getVacationEventsByEmployee()
// - getVacationEventsByDepartment()
// - getVacationEventsByStatus()
// - getCurrentWeekVacationEvents()
// - getUpcomingVacationEvents()
// Use listMyVacationRequests() API function instead

// ============================================================================
// 🚀 VACATION BALANCE FUNCTIONS
// ============================================================================

// Removed: All helper functions that used TECH_TEAM_VACATION constant
// - getVacationBalanceByEmployee()
// - getVacationBalancesByDepartment()
// - getHighVacationBalanceEmployees()
// - getLowVacationBalanceEmployees()
// - getVacationBalanceStatistics()
// Use getMyVacationBalance() API function instead

// ============================================================================
// 🚀 VACATION ANALYTICS FUNCTIONS
// ============================================================================

// Removed: All analytics functions that used mock data constants
// - getVacationTrends()
// - getDepartmentVacationUtilization()
// - getVacationRecommendations()
// Use real API data from getMyVacationBalance() and listMyVacationRequests() instead

// ============================================================================
// 🚀 NEW VACATION API ENDPOINTS (VAC-6)
// ============================================================================

/**
 * Get vacation balance for a specific employee (or current user if no employeeId provided)
 * Uses /workforce/vacation/balance endpoint with optional employeeId query parameter
 */
export const getVacationBalance = async (employeeId?: string): Promise<VacationBalance> => {
  try {
    const queryParams = new URLSearchParams()
    if (employeeId) {
      queryParams.append('employeeId', employeeId)
    }
    const queryString = queryParams.toString()
    const path = `/workforce/vacation/balance${queryString ? `?${queryString}` : ''}`
    logApiCall(path)

    const { response } = await get({
      apiName: 'workforce',
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const result = await response
    return await result.body.json() as VacationBalance
  } catch (error: any) {
    console.error('[getVacationBalance] Error fetching vacation balance:', error)

    if (error.response?.status === 403) {
      throw new VacationError('FORBIDDEN', 'Sem permissão para visualizar saldo de férias')
    }

    if (error.response?.status === 404) {
      throw new VacationError('NOT_FOUND', 'Saldo de férias não encontrado')
    }

    throw new VacationError('NETWORK_ERROR', 'Erro ao buscar saldo de férias', { originalError: error.message })
  }
}

/**
 * Get current user's vacation balance (backward compatibility)
 */
export const getMyVacationBalance = async (): Promise<VacationBalance> => {
  return getVacationBalance()
}

/**
 * Get current user's vacation requests with pagination
 * Uses /workforce/vacation/requests/me endpoint
 */
export const getMyVacationRequests = async (limit: number = 5): Promise<VacationRequestsResponse> => {
  try {
    const queryParams = new URLSearchParams({ limit: limit.toString() })
    const path = `/workforce/vacation/requests/me?${queryParams.toString()}`
    logApiCall(path)

    const { response } = await get({
      apiName: 'workforce',
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const result = await response
    const data = await result.body.json()

    // Parse pagination headers
    const pagination: { nextToken?: string; itemCount: number } = {
      itemCount: parseInt(result.headers.get('x-item-count') || '0', 10)
    }

    const nextToken = result.headers.get('x-next-token')
    if (nextToken) {
      pagination.nextToken = nextToken
    }

    return {
      requests: data.requests || data,
      pagination
    }
  } catch (error: any) {
    console.error('[getMyVacationRequests] Error fetching vacation requests:', error)

    if (error.response?.status === 403) {
      throw new VacationError('FORBIDDEN', 'Sem permissão para visualizar solicitações de férias')
    }

    throw new VacationError('NETWORK_ERROR', 'Erro ao buscar solicitações de férias', { originalError: error.message })
  }
}

// ============================================================================
// 🚀 VAC-7: VACATION REQUEST SUBMISSION ENDPOINTS
// ============================================================================

import type {
  VacationRequest,
  VacationRequestStatus,
  CreateVacationRequestInput
} from '@/types/vacation'
import { post } from '@/api/lib/amplify-wrapper'

/**
 * Custom error classes for vacation operations
 */
export class VacationError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: Record<string, any>
  ) {
    super(message)
    this.name = 'VacationError'
  }
}

export class ValidationError extends VacationError {
  constructor(message: string, details?: Record<string, any>) {
    super('VALIDATION_ERROR', message, details)
  }
}

export class InsufficientBalanceError extends VacationError {
  constructor(available: number, requested: number) {
    super(
      'INSUFFICIENT_BALANCE',
      `Saldo insuficiente. Disponível: ${available} dias, Solicitado: ${requested} dias`,
      { available, requested }
    )
  }
}

/**
 * Create a new vacation request
 * POST /workforce/vacation/requests
 */
export const createVacationRequest = async (
  input: CreateVacationRequestInput
): Promise<VacationRequest> => {
  try {
    const path = '/workforce/vacation/requests'
    logApiCall(path)

    const { response } = await post({
      apiName: 'workforce',
      path,
      options: {
        headers: createImpersonatedApiHeaders(),
        body: input
      }
    })

    const result = await response
    return await result.body.json() as VacationRequest
  } catch (error: any) {
    console.error('[createVacationRequest] Error creating vacation request:', error)

    // Handle backend validation errors
    if (error.response?.status === 400) {
      const errorData = error.response?.body ? await error.response.body.json() : {}
      const errorMessage = errorData.message || 'Erro de validação'

      // Check if it's an insufficient balance error
      if (errorMessage.toLowerCase().includes('insufficient') || errorMessage.toLowerCase().includes('saldo')) {
        throw new InsufficientBalanceError(0, 0) // Backend should provide actual values
      }

      throw new ValidationError(errorMessage, errorData)
    }

    if (error.response?.status === 403) {
      throw new VacationError('FORBIDDEN', 'Sem permissão para criar solicitação de férias')
    }

    throw new VacationError('NETWORK_ERROR', 'Erro ao criar solicitação de férias', { originalError: error.message })
  }
}

/**
 * List my vacation requests with optional status filter
 * GET /workforce/vacation/requests/me?status=PENDING
 */
export const listMyVacationRequests = async (
  status?: VacationRequestStatus
): Promise<{ items: VacationRequest[] }> => {
  try {
    const queryParams = new URLSearchParams()
    if (status) {
      queryParams.append('status', status)
    }

    const queryString = queryParams.toString()
    const path = `/workforce/vacation/requests/me${queryString ? `?${queryString}` : ''}`
    logApiCall(path)

    const { response } = await get({
      apiName: 'workforce',
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const result = await response
    const data = await result.body.json()

    return {
      items: data.requests || data.items || data
    }
  } catch (error: any) {
    console.error('[listMyVacationRequests] Error listing vacation requests:', error)

    if (error.response?.status === 403) {
      throw new VacationError('FORBIDDEN', 'Sem permissão para listar solicitações de férias')
    }

    throw new VacationError('NETWORK_ERROR', 'Erro ao listar solicitações de férias', { originalError: error.message })
  }
}

/**
 * Get specific vacation request by ID
 * GET /workforce/vacation/requests/{requestId}
 */
export const getVacationRequest = async (requestId: string): Promise<VacationRequest> => {
  try {
    const path = `/workforce/vacation/requests/${requestId}`
    logApiCall(path)

    const { response } = await get({
      apiName: 'workforce',
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const result = await response
    return await result.body.json() as VacationRequest
  } catch (error: any) {
    console.error('[getVacationRequest] Error fetching vacation request:', error)

    if (error.response?.status === 404) {
      throw new VacationError('NOT_FOUND', 'Solicitação não encontrada')
    }

    if (error.response?.status === 403) {
      throw new VacationError('FORBIDDEN', 'Sem permissão para visualizar esta solicitação')
    }

    throw new VacationError('NETWORK_ERROR', 'Erro ao buscar solicitação de férias', { originalError: error.message })
  }
}

/**
 * Cancel a pending vacation request
 * DELETE /workforce/vacation/requests/{requestId}
 */
export const cancelVacationRequest = async (requestId: string): Promise<void> => {
  try {
    const path = `/workforce/vacation/requests/${requestId}`
    logApiCall(path)

    const { response } = await del({
      apiName: 'workforce',
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    await response // Await the response to ensure completion
  } catch (error: any) {
    console.error('[cancelVacationRequest] Error cancelling vacation request:', error)

    if (error.response?.status === 404) {
      throw new VacationError('NOT_FOUND', 'Solicitação não encontrada')
    }

    if (error.response?.status === 403) {
      throw new VacationError('FORBIDDEN', 'Não é possível cancelar esta solicitação')
    }

    throw new VacationError('NETWORK_ERROR', 'Erro ao cancelar solicitação de férias', { originalError: error.message })
  }
}

/**
 * Get vacation events by month
 * This function provides backward compatibility for components that expect the old getVacationEventsByMonth function
 * It uses the new API endpoints to fetch vacation requests and formats them as vacation events
 */
export const getVacationEventsByMonth = async (year: number, month: number): Promise<VacationEvent[]> => {
  try {
    // Get vacation requests for the current user
    const requests = await listMyVacationRequests()
    
    // Filter requests by the specified month and year
    const filteredRequests = requests.items.filter(request => {
      const startDate = new Date(request.startDate)
      const requestYear = startDate.getFullYear()
      const requestMonth = startDate.getMonth() + 1 // getMonth() returns 0-11, we need 1-12
      
      return requestYear === year && requestMonth === month
    })

    // Convert vacation requests to vacation events format
    const events: VacationEvent[] = filteredRequests.map(request => ({
      id: request.id,
      employeeName: request.employeeName || 'Funcionário',
      department: request.department || 'Departamento',
      type: request.type === 'VACATION' ? 'scheduled' : 
            request.type === 'SICK_LEAVE' ? 'leave' : 'timeoff',
      startDate: request.startDate,
      endDate: request.endDate,
      days: request.days,
      status: request.status === 'APPROVED' ? 'approved' :
              request.status === 'PENDING' ? 'pending' : 'rejected',
      description: request.reason || 'Solicitação de férias'
    }))

    return events
  } catch (error: any) {
    console.error('[getVacationEventsByMonth] Error fetching vacation events:', error)
    
    // Return empty array on error to prevent component crashes
    return []
  }
}